/**
 * The bridge between the on-screen stick (the React overlay, `ui/touch/`) and the game's
 * `InputState` (mobile-layout.md §7.1). One shared holder, the `explicitTarget` pattern: the
 * overlay writes the stick's direction, `InputState.sample` reads it each step, and neither
 * owns the other. Pure (no DOM), so `InputState` tests can drive it directly.
 */

export interface StickDirection {
  /** Unit direction in stick space (+x screen-right, +z forward); both 0 when idle. */
  ix: number;
  iz: number;
}

export class StickChannel {
  private ix = 0;
  private iz = 0;

  /** The stick is past its dead zone, i.e. the player is steering with it. */
  get active(): boolean {
    return this.ix !== 0 || this.iz !== 0;
  }

  get direction(): StickDirection {
    return { ix: this.ix, iz: this.iz };
  }

  /** Set the direction (a non-finite component reads as idle). */
  set(ix: number, iz: number): void {
    if (!Number.isFinite(ix) || !Number.isFinite(iz)) {
      this.clear();
      return;
    }
    this.ix = ix;
    this.iz = iz;
  }

  clear(): void {
    this.ix = 0;
    this.iz = 0;
  }
}

/** The page's one stick channel (the overlay writes, `InputState` reads). */
export const stickChannel = new StickChannel();
